/**
 * The typed-filter grammar of Discover's search box (jorigin/brewlytics#2290).
 *
 * The box's text is the one source of truth for a search: the Filters panel,
 * the chips and the URL all read it through `parseSearch` and write it back
 * through `replaceField` / `removeToken`, so the three views can never drift.
 *
 * Grammar (whitespace separates tokens; `"…"` quotes a value with spaces, `\"`
 * escapes a quote inside one; keys are case-insensitive):
 *
 *   tag:port-ops          has the tag (several `tag:` tokens: has all of them)
 *   tag:ais|vessel        has any of these tags (one such group per search)
 *   -tag:draft            does not have the tag
 *   author:me             owned by me; `author:"Jane Doe"` by a person (looked
 *                         up by name), `author:<profile id>`; several: any of them
 *   updated:30d           7d, 30d, 90d or 1y; or dates: `>=2026-01-01`,
 *   created:>2026-01-01   `>D` (after that day), `<=D`, `<D`, `D` (that day),
 *                         `D..E` (both days included), `D..`, `..E`
 *   is:pick               editor's picks only
 *   is:shared             shared with me
 *   group:"Port Ops"      shared with that group (name or group id)
 *   uses:"Geo Buffer"     uses that functional (name or functional id)
 *   uses:A+B|C            uses (A and B) or C: `+` is and, `|` an alternative,
 *                         `+` binds tighter; several `uses:` tokens are ANDed,
 *                         so `uses:A uses:B|C` is (A and B) or (A and C) (#2299)
 *   submodel:"Port Calls" uses that model inside it (name or model id), with
 *                         `+` and `|` as for `uses:`
 *   sort:updated          relevance, updated, created, name or runs
 *
 * Anything else is plain search text, including words with a colon whose
 * prefix is not a key above (`note:berth`, `http://…`). A known key with a
 * bad value is an `invalid` token: it is shown as such and left out of the
 * search, never searched as text.
 *
 * The token the user is typing right now (`active`) is shown but not yet
 * applied: it applies, and becomes a chip, once a space follows it, the caret
 * leaves it, Enter is pressed or the box loses focus.
 */
import {
  MAX_QUERY_AUTHORS,
  MAX_QUERY_REFS,
  MAX_QUERY_TAGS,
  MAX_QUERY_TEXT,
  MAX_QUERY_YEARS_BACK,
  MODEL_QUERY_VERSION,
  QUERY_ID_PATTERN,
  QUERY_SORTS,
  QUERY_TAG_PATTERN,
  QUERY_WITHIN,
  type ModelQuery,
  type QueryRange,
  type QuerySort,
  type QueryWithin,
} from '@/types/modelQuery';
import { compareCodeUnits } from '@/utils/compare';

// ── Keys, fields and kinds ──────────────────────────────────────────────

export type FilterKey = 'tag' | 'author' | 'updated' | 'created' | 'is' | 'uses' | 'submodel' | 'group' | 'sort';

export const FILTER_KEYS: readonly FilterKey[] = ['tag', 'author', 'updated', 'created', 'is', 'uses', 'submodel', 'group', 'sort'];

/** The part of the query a token sets: what the panel edits and a chip opens. */
export type Field =
  | 'text'
  | 'tags.all'
  | 'tags.any'
  | 'tags.none'
  | 'authors'
  | 'updated'
  | 'created'
  | 'pick'
  | 'shared'
  | 'uses'
  | 'submodels'
  | 'sort';

/** The fields of the ids a model's components must use (#2299). */
export type RefsField = 'uses' | 'submodels';

/** How a token is coloured. */
export type TokenKind = 'tag' | 'exclude' | 'author' | 'date' | 'flag' | 'shared' | 'uses' | 'submodel' | 'sort';

export type TokenStatus = 'valid' | 'pending' | 'incomplete' | 'invalid';

export type DateFilter = { within: QueryWithin } | { from?: string; to?: string };

/** A search as the UI holds it: ids, never names; days inclusive. */
export interface SearchQuery {
  text: string;
  tags: { all: string[]; any: string[]; none: string[] };
  /** `me` or profile ids (lower case). */
  authors: string[];
  updated: DateFilter | null;
  created: DateFilter | null;
  editorsPick: boolean;
  /** `me` or a group id. */
  shared: string | null;
  /** Functionals a model must use: every id of at least one group (#2299). */
  functionals: Groups;
  /** Models a model must use as submodels, the same way. */
  submodels: Groups;
  sort: QuerySort | null;
}

/**
 * Alternatives, each a group of ids (lower case) a model must all use:
 * `[[a, b], [c]]` is (a and b) or c. Empty: no filter.
 */
export type Groups = string[][];

/** At most this many alternatives per kind, as the server allows. */
export const MAX_QUERY_GROUPS = 5;

export const EMPTY_QUERY: SearchQuery = {
  text: '',
  tags: { all: [], any: [], none: [] },
  authors: [],
  updated: null,
  created: null,
  editorsPick: false,
  shared: null,
  functionals: [],
  submodels: [],
  sort: null,
};

export const SORT_LABELS: Record<QuerySort, string> = {
  relevance: 'Best match',
  updated: 'Recently updated',
  created: 'Newest',
  name: 'Name (A–Z)',
  runs: 'Most run this week',
};

export const WITHIN_LABELS: Record<QueryWithin, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  '1y': 'Last year',
};

// ── Entity lookups ──────────────────────────────────────────────────────

/** What a name in `author:`, `uses:`, `submodel:` or `group:` stands for. */
export type EntityKind = 'author' | 'functional' | 'model' | 'group';

export type Resolution =
  | { status: 'resolved'; id: string }
  | { status: 'pending' }
  | { status: 'none' }
  | { status: 'ambiguous' }
  | { status: 'failed' };

/** Names to ids and ids to labels, as far as they are known (see `useEntityResolver`). */
export interface EntityLookup {
  byName(kind: EntityKind, name: string): Resolution;
  labelOf(kind: EntityKind, id: string): string | undefined;
  /** The name to write for an id: its label, when that label leads back to this id alone. */
  nameFor(kind: EntityKind, id: string): string | undefined;
}

/** A lookup that knows nothing: every name is pending. */
export const NO_LOOKUP: EntityLookup = {
  byName: () => ({ status: 'pending' }),
  labelOf: () => undefined,
  nameFor: () => undefined,
};

// ── Lexing ──────────────────────────────────────────────────────────────

export interface Lexeme {
  start: number;
  end: number;
  raw: string;
  /** A quote opened and never closed. */
  unclosed: boolean;
}

const isSpace = (c: string) => /\s/.test(c);

/** Split on whitespace outside quotes. */
export function lex(text: string): Lexeme[] {
  const out: Lexeme[] = [];
  let i = 0;
  const n = text.length;
  while (i < n) {
    while (i < n && isSpace(text[i])) i++;
    if (i >= n) break;
    const start = i;
    let quoted = false;
    while (i < n) {
      const c = text[i];
      if (quoted) {
        if (c === '\\' && i + 1 < n) { i += 2; continue; }
        if (c === '"') quoted = false;
        i++;
        continue;
      }
      if (isSpace(c)) break;
      if (c === '"') quoted = true;
      i++;
    }
    out.push({ start, end: i, raw: text.slice(start, i), unclosed: quoted });
  }
  return out;
}

const KEY_RE = /^(-?)([A-Za-z]+):/;

/** The key a lexeme starts with, when it is one of ours. */
export function keyOf(raw: string): { key: FilterKey; negated: boolean; valueStart: number } | null {
  const m = KEY_RE.exec(raw);
  if (!m) return null;
  const key = m[2].toLowerCase() as FilterKey;
  if (!FILTER_KEYS.includes(key)) return null;
  return { key, negated: m[1] === '-', valueStart: m[0].length };
}

/** One part of a value: its text, where it ended, and what was wrong with it. */
interface Part {
  part: string;
  end: number;
  /** The part opened a quote and never closed it. */
  unclosed: boolean;
  /** Text after a closing quote, or a stray quote in a bare part. */
  malformed: boolean;
}

/** The first separator at or after `i`, or the end. */
function nextSep(value: string, i: number, isSep: (c: string) => boolean): number {
  while (i < value.length && !isSep(value[i])) i++;
  return i;
}

/** The text of a `"quoted"` part from `start` (just past its opening quote); a backslash keeps the next character as is. */
function readQuoted(value: string, start: number): { part: string; end: number; closed: boolean } {
  let part = '';
  let i = start;
  while (i < value.length) {
    const c = value[i];
    if (c === '\\' && i + 1 < value.length) { part += value[i + 1]; i += 2; continue; }
    if (c === '"') return { part, end: i + 1, closed: true };
    part += c;
    i++;
  }
  return { part, end: i, closed: false };
}

/** The part at `start`, bare or `"quoted"`, up to the next separator. */
function readPart(value: string, start: number, isSep: (c: string) => boolean): Part {
  if (value[start] !== '"') {
    const end = nextSep(value, start, isSep);
    const part = value.slice(start, end);
    return { part, end, unclosed: false, malformed: part.includes('"') };
  }
  const q = readQuoted(value, start + 1);
  if (!q.closed) return { part: q.part, end: q.end, unclosed: true, malformed: false };
  const end = nextSep(value, q.end, isSep);
  return { part: q.part, end, unclosed: false, malformed: end > q.end };
}

/**
 * A value's parts: one, or for `tag:` several separated by `|`. Each part is
 * bare or `"quoted"`. `unclosed`: a quote is still open (being typed);
 * `malformed`: text after a closing quote, or a stray quote.
 */
export function readValue(value: string, pipes: boolean): { parts: string[]; unclosed: boolean; malformed: boolean } {
  const parts: string[] = [];
  const isSep = (c: string) => pipes && c === '|';
  let i = 0;
  let malformed = false;
  for (;;) {
    const r = readPart(value, i, isSep);
    parts.push(r.part);
    if (r.unclosed) return { parts, unclosed: true, malformed };
    if (r.malformed) malformed = true;
    i = r.end;
    if (i >= value.length) break;
    i++; // the pipe
  }
  return { parts, unclosed: false, malformed };
}

/** A value written so `readValue` reads it back: quoted when it has to be. */
export function quote(value: string): string {
  if (value !== '' && !/[\s"|\\]/.test(value)) return value;
  const escaped = value.replace(/\\/g, String.raw`\\`).replace(/"/g, String.raw`\"`);
  return `"${escaped}"`;
}

/** A name in `uses:` or `submodel:`, quoted when it has to be: a `+` joins names there. */
export function quoteRef(value: string): string {
  return /\+/.test(value) && !/[\s"|\\]/.test(value) ? `"${value}"` : quote(value);
}

/**
 * A `uses:` or `submodel:` value (#2299): alternatives separated by `|`, each
 * names joined by `+` (`+` binds tighter). Each name is bare or `"quoted"`.
 * `empty`: a name is missing, before or after an operator; `unclosed` and
 * `malformed` as for `readValue`.
 */
export function readGroups(value: string): { groups: string[][]; unclosed: boolean; malformed: boolean; empty: boolean } {
  const groups: string[][] = [[]];
  const sep = (c: string) => c === '+' || c === '|';
  let i = 0;
  let malformed = false;
  let empty = false;
  for (;;) {
    const r = readPart(value, i, sep);
    if (r.unclosed) {
      groups.at(-1)!.push(r.part);
      return { groups, unclosed: true, malformed, empty };
    }
    if (r.malformed) malformed = true;
    i = r.end;
    if (r.part.trim() === '') empty = true;
    groups.at(-1)!.push(r.part.trim());
    if (i >= value.length) break;
    if (value[i] === '|') groups.push([]);
    i++;
  }
  return { groups, unclosed: false, malformed, empty };
}

/** A group of names as a `uses:`/`submodel:` value. */
export function writeGroups(groups: string[][]): string {
  return groups.map((g) => g.map(quoteRef).join('+')).join('|');
}

/** Two sets of alternatives ANDed: each group of one with each of the other, repeats dropped. */
export function andGroups(a: Groups, b: Groups): Groups {
  if (!a.length) return dedupeGroups(b);
  if (!b.length) return dedupeGroups(a);
  return dedupeGroups(a.flatMap((ga) => b.map((gb) => [...ga, ...gb])));
}

/** Ids once per group, and a group with the same ids as an earlier one dropped. */
export function dedupeGroups(groups: Groups): Groups {
  const out: Groups = [];
  const seen = new Set<string>();
  for (const g of groups) {
    const ids = [...new Set(g)];
    const key = [...ids].sort(compareCodeUnits).join('\n');
    if (ids.length && !seen.has(key)) { seen.add(key); out.push(ids); }
  }
  return out;
}

/** Alternatives in words: "(Geo Buffer and Lookup) or Join". */
export function describeGroups(groups: Groups, label: (id: string) => string): string {
  const words = groups.map((g) => g.map(label).join(' and '));
  return groups.length > 1 ? words.map((w, i) => (groups[i].length > 1 ? `(${w})` : w)).join(' or ') : words.join('');
}

// ── Dates ───────────────────────────────────────────────────────────────

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

function dayValue(day: string): number | null {
  if (!DAY_RE.test(day)) return null;
  const [y, m, d] = day.split('-').map(Number);
  const t = Date.UTC(y, m - 1, d);
  const back = new Date(t);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
  return t;
}

function dayString(t: number): string {
  return new Date(t).toISOString().slice(0, 10);
}

/** `day` moved by `n` days. */
export function addDays(day: string, n: number): string {
  const t = dayValue(day);
  return t === null ? day : dayString(t + n * 86_400_000);
}

/** Today's day in UTC, the zone the server reads a day in. */
export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/**
 * A date value: a window, or a range of whole days (both ends included).
 * Returns an error message instead when it is not one.
 */
export function parseDateValue(value: string, today: string): DateFilter | string {
  const v = value.trim();
  if ((QUERY_WITHIN as readonly string[]).includes(v.toLowerCase())) return { within: v.toLowerCase() as QueryWithin };

  let from: string | undefined;
  let to: string | undefined;
  const range = /^([^.]*)\.\.([^.]*)$/.exec(v);
  if (range) {
    const a = range[1] === '*' ? '' : range[1];
    const b = range[2] === '*' ? '' : range[2];
    if (!a && !b) return 'Give a start or an end date: 2026-01-01..2026-02-01';
    from = a || undefined;
    to = b || undefined;
  } else {
    const op = /^(>=|<=|>|<)?(.*)$/.exec(v)!;
    const day = op[2];
    switch (op[1]) {
      case '>=': from = day; break;
      case '>': from = dayValue(day) === null ? day : addDays(day, 1); break;
      case '<=': to = day; break;
      case '<': to = dayValue(day) === null ? day : addDays(day, -1); break;
      default: from = day; to = day;
    }
  }
  for (const d of [from, to]) {
    if (d !== undefined && dayValue(d) === null) {
      return 'Use 7d, 30d, 90d, 1y or a date like 2026-01-31 (with >=, <=, > or <, or from..to)';
    }
  }
  const earliest = dayValue(addYears(today, -MAX_QUERY_YEARS_BACK))!;
  if ((from !== undefined && dayValue(from)! < earliest) || (to !== undefined && dayValue(to)! < earliest)) {
    return `Dates can go back at most ${MAX_QUERY_YEARS_BACK} years`;
  }
  if (from !== undefined && to !== undefined && dayValue(from)! > dayValue(to)!) {
    return 'The start date is after the end date';
  }
  return { ...(from !== undefined ? { from } : {}), ...(to !== undefined ? { to } : {}) };
}

function addYears(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number);
  // Feb 29 minus a year is Mar 1 in UTC arithmetic: close enough for a limit.
  return dayString(Date.UTC(y + n, m - 1, d));
}

/** A date filter as typed: the canonical form `parseDateValue` reads back. */
export function formatDateValue(f: DateFilter): string {
  if ('within' in f) return f.within;
  if (f.from && f.to) return f.from === f.to ? f.from : `${f.from}..${f.to}`;
  if (f.from) return `>=${f.from}`;
  if (f.to) return `<=${f.to}`;
  return '';
}

/** A date filter in words, for a chip. */
export function describeDate(f: DateFilter): string {
  if ('within' in f) return WITHIN_LABELS[f.within].replace(/^Last/, 'last');
  if (f.from && f.to) return f.from === f.to ? `on ${f.from}` : `${f.from} to ${f.to}`;
  if (f.from) return `since ${f.from}`;
  return `until ${f.to}`;
}

// ── Tokens ──────────────────────────────────────────────────────────────

export type TokenValue =
  | { field: 'tags.all' | 'tags.any' | 'tags.none'; tags: string[] }
  | { field: 'authors'; id: string }
  | { field: 'updated' | 'created'; date: DateFilter }
  | { field: 'pick' }
  | { field: 'shared'; id: string }
  | { field: RefsField; groups: Groups }
  | { field: 'sort'; sort: QuerySort };

export interface Token extends Lexeme {
  type: 'word' | 'filter';
  key?: FilterKey;
  negated?: boolean;
  /** The field it sets, when its key and value say which. */
  field?: Field;
  kind?: TokenKind;
  status: TokenStatus;
  /** Why it is invalid, incomplete or pending. */
  message?: string;
  /** What it adds to the search, when valid. */
  value?: TokenValue;
  /** Being typed: shown, not yet applied. */
  active: boolean;
  /** The chip's words, when valid: "Tag" · "port-ops". */
  chip?: { name: string; value: string };
  /** The people, functionals, models or groups it names, by name or id as typed. */
  entities?: { kind: EntityKind; name: string }[];
  /** `uses:`/`submodel:`: the labels of the ids it names, for the chips (#2299). */
  labels?: Record<string, string>;
  /** A chip of one alternative of `uses`/`submodels` (#2299): its index in the search's groups. */
  group?: number;
}

export interface ParsedSearch {
  tokens: Token[];
  /** The search the valid, settled tokens make. */
  query: SearchQuery;
  /** A name is still being looked up: hold the search. */
  pending: boolean;
  /** Plain-words problems with the whole search, e.g. text too long. */
  problems: string[];
}

export interface ParseOptions {
  lookup?: EntityLookup;
  /** The caret, and where the last edit left it: the token holding both is being typed. */
  caret?: number | null;
  editAt?: number | null;
  /** Today in UTC (YYYY-MM-DD), for the ten-years-back limit. */
  today?: string;
}

const SINGLE_FIELDS = new Set<Field>(['updated', 'created', 'shared', 'sort']);

/** The keys whose value is groups of names joined by `+` and `|` (#2299). */
const GROUPED: ReadonlySet<FilterKey> = new Set<FilterKey>(['uses', 'submodel']);

/** Whether a key's value is groups of names, `a+b|c`. */
export function takesGroups(key: FilterKey): boolean {
  return GROUPED.has(key);
}

function fieldOf(key: FilterKey, negated: boolean, value: string): Field | undefined {
  switch (key) {
    case 'tag':
      if (negated) return 'tags.none';
      return readValue(value, true).parts.length > 1 ? 'tags.any' : 'tags.all';
    case 'author': return 'authors';
    case 'updated': return 'updated';
    case 'created': return 'created';
    case 'is': {
      const v = value.toLowerCase();
      if (v === 'pick') return 'pick';
      return v === 'shared' ? 'shared' : undefined;
    }
    case 'group': return 'shared';
    case 'uses': return 'uses';
    case 'submodel': return 'submodels';
    case 'sort': return 'sort';
  }
}

function kindOf(key: FilterKey, negated: boolean, field: Field | undefined): TokenKind {
  if (key === 'tag') return negated ? 'exclude' : 'tag';
  if (key === 'author') return 'author';
  if (key === 'updated' || key === 'created') return 'date';
  if (key === 'is') return field === 'shared' ? 'shared' : 'flag';
  if (key === 'group') return 'shared';
  if (key === 'uses') return 'uses';
  if (key === 'submodel') return 'submodel';
  return 'sort';
}

const ENTITY_OF: Partial<Record<FilterKey, EntityKind>> = { author: 'author', uses: 'functional', submodel: 'model', group: 'group' };
const ENTITY_WORD: Record<EntityKind, string> = { author: 'person', functional: 'functional', model: 'model', group: 'group' };

/** A chip's name for each refs field: the first alternative's, and the others'. */
const REFS_CHIP: Record<RefsField, [string, string]> = { uses: ['Uses', 'Or uses'], submodels: ['Submodel', 'Or submodel'] };

/** A `uses:` or `submodel:` value: its groups of names, each looked up (#2299). */
function readRefs(base: Omit<Token, 'active' | 'status'>, key: 'uses' | 'submodel', rawValue: string,
  o: Required<Pick<ParseOptions, 'lookup' | 'today'>>): Omit<Token, 'active'> {
  const entity = ENTITY_OF[key]!;
  const word = ENTITY_WORD[entity];
  const { groups: names, unclosed, malformed, empty } = readGroups(rawValue);
  const named = (t: Omit<Token, 'active'>): Omit<Token, 'active'> =>
    ({ ...t, entities: names.flat().filter(Boolean).map((name) => ({ kind: entity, name })) });
  const bad = (message: string) => named({ ...base, status: 'invalid', message });
  const incomplete = (message: string) => named({ ...base, status: 'incomplete', message });
  if (unclosed) return incomplete('Close the quote');
  if (malformed) return bad(`Put each name in quotes: ${key}:"Geo Buffer"+Lookup`);
  if (names.length === 1 && names[0].length === 1 && names[0][0] === '') return incomplete(`Type a value after ${key}:`);
  if (empty) return incomplete(`A name is missing beside a + or |: ${key}:A+B|C`);
  if (names.length > MAX_QUERY_GROUPS) return bad(`At most ${MAX_QUERY_GROUPS} alternatives (|)`);
  if (names.some((g) => g.length > MAX_QUERY_REFS)) return bad(`At most ${MAX_QUERY_REFS} ${word}s joined by +`);

  const labels: Record<string, string> = {};
  const groups: Groups = [];
  let waiting: string | undefined;
  for (const g of names) {
    const ids: string[] = [];
    for (const name of g) {
      const r: Resolution = QUERY_ID_PATTERN.test(name) ? { status: 'resolved', id: name.toLowerCase() } : o.lookup.byName(entity, name);
      switch (r.status) {
        case 'resolved':
          ids.push(r.id);
          labels[r.id] = o.lookup.labelOf(entity, r.id) ?? (QUERY_ID_PATTERN.test(name) ? '…' : name);
          break;
        case 'pending': waiting ??= name; break;
        case 'ambiguous': return bad(`More than one ${word} matches "${name}": pick one from the list`);
        case 'failed': return bad(`Couldn't look up "${name}"`);
        default: return bad(`No ${word} matches "${name}"`);
      }
    }
    groups.push(ids);
  }
  if (waiting !== undefined) return named({ ...base, status: 'pending', message: `Looking up the ${word} "${waiting}"…` });
  const field = base.field as RefsField;
  const kept = dedupeGroups(groups);
  return {
    ...named({ ...base, status: 'valid', value: { field, groups: kept } }),
    labels,
    chip: { name: REFS_CHIP[field][0], value: describeGroups(kept, (id) => labels[id]) },
  };
}

/** A filter token before its status: what `readFilter` builds on. */
type FilterBase = Omit<Token, 'active' | 'status'>;

const invalidToken = (base: FilterBase, message: string): Omit<Token, 'active'> => ({ ...base, status: 'invalid', message });
const incompleteToken = (base: FilterBase, message: string): Omit<Token, 'active'> => ({ ...base, status: 'incomplete', message });
const validToken = (base: FilterBase, value: TokenValue, chip: { name: string; value: string }): Omit<Token, 'active'> =>
  ({ ...base, status: 'valid', value, chip });

/** `tag:` or `-tag:`: one tag, or an either-or group of them. */
function readTags(base: FilterBase, parts: string[]): Omit<Token, 'active'> {
  const tags = parts.map((p) => p.trim());
  const wrong = tags.find((t) => !QUERY_TAG_PATTERN.test(t));
  if (wrong !== undefined) {
    return wrong === ''
      ? incompleteToken(base, 'Type a tag after the |')
      : invalidToken(base, `"${wrong}" is not a tag: letters, digits, spaces and _ . : / + # & ' - only, at most 64`);
  }
  if (tags.length > MAX_QUERY_TAGS) return invalidToken(base, `At most ${MAX_QUERY_TAGS} tags in a group`);
  const f = base.field as 'tags.all' | 'tags.any' | 'tags.none';
  let name = 'Tag';
  if (f === 'tags.none') name = 'Not tagged';
  else if (f === 'tags.any') name = 'Any tag';
  return validToken(base, { field: f, tags }, { name, value: tags.join(' or ') });
}

/** `updated:` or `created:`: a window or a range of days. */
function readDate(base: FilterBase, key: 'updated' | 'created', value: string, today: string): Omit<Token, 'active'> {
  if (/^(>=|<=|>|<|\.\.)$/.test(value.trim())) return incompleteToken(base, 'Type a date, like 2026-01-31');
  const date = parseDateValue(value, today);
  if (typeof date === 'string') return invalidToken(base, date);
  return validToken(base, { field: key, date }, { name: key === 'updated' ? 'Updated' : 'Created', value: describeDate(date) });
}

/** `author:` or `group:`: `me` (authors only), an id, or a name to look up. */
function readEntity(base: FilterBase, key: 'author' | 'group', name: string, lookup: EntityLookup): Omit<Token, 'active'> {
  const entity = ENTITY_OF[key]!;
  const chipName = key === 'author' ? 'Author' : 'Shared with';
  const named = (t: Omit<Token, 'active'>): Omit<Token, 'active'> => ({ ...t, entities: [{ kind: entity, name }] });
  const make = (id: string): Omit<Token, 'active'> => {
    let label = 'Me';
    if (id !== 'me') label = lookup.labelOf(entity, id) ?? (QUERY_ID_PATTERN.test(name) ? '…' : name);
    const value: TokenValue = key === 'author' ? { field: 'authors', id } : { field: 'shared', id };
    return named(validToken(base, value, { name: chipName, value: label }));
  };
  if (key === 'author' && name.toLowerCase() === 'me') return make('me');
  if (QUERY_ID_PATTERN.test(name)) return make(name.toLowerCase());
  const r = lookup.byName(entity, name);
  switch (r.status) {
    case 'resolved': return make(r.id);
    case 'pending': return named({ ...base, status: 'pending', message: `Looking up the ${ENTITY_WORD[entity]} "${name}"…` });
    case 'ambiguous': return named(invalidToken(base, `More than one ${ENTITY_WORD[entity]} matches "${name}": pick one from the list`));
    case 'failed': return named(invalidToken(base, `Couldn't look up "${name}"`));
    default: return named(invalidToken(base, `No ${ENTITY_WORD[entity]} matches "${name}"`));
  }
}

/** Read one filter lexeme on its own; cross-token rules come after. */
function readFilter(lx: Lexeme, key: FilterKey, negated: boolean, valueStart: number, o: Required<Pick<ParseOptions, 'lookup' | 'today'>>): Omit<Token, 'active'> {
  const rawValue = lx.raw.slice(valueStart);
  const field = fieldOf(key, negated, rawValue);
  const base = { ...lx, type: 'filter' as const, key, negated, field, kind: kindOf(key, negated, field) };

  if (negated && key !== 'tag') return invalidToken(base, 'Only tags can be excluded: -tag:draft');
  if (key === 'uses' || key === 'submodel') return readRefs(base, key, rawValue, o);
  const { parts, unclosed, malformed } = readValue(rawValue, key === 'tag');
  if (unclosed) return incompleteToken(base, 'Close the quote');
  if (malformed) return invalidToken(base, 'Put the whole value in quotes: author:"Jane Doe"');
  if (parts.length === 1 && parts[0].trim() === '') return incompleteToken(base, `Type a value after ${negated ? '-' : ''}${key}:`);

  switch (key) {
    case 'tag': return readTags(base, parts);
    case 'updated':
    case 'created': return readDate(base, key, parts[0], o.today);
    case 'is': {
      if (field === 'pick') return validToken(base, { field: 'pick' }, { name: "Editor's picks", value: '' });
      if (field === 'shared') return validToken(base, { field: 'shared', id: 'me' }, { name: 'Shared with me', value: '' });
      return invalidToken(base, 'Use is:pick or is:shared');
    }
    case 'sort': {
      const s = parts[0].toLowerCase() as QuerySort;
      if (!QUERY_SORTS.includes(s)) return invalidToken(base, 'Sort by relevance, updated, created, name or runs');
      return validToken(base, { field: 'sort', sort: s }, { name: 'Sort', value: SORT_LABELS[s] });
    }
    case 'author':
    case 'group': return readEntity(base, key, parts[0].trim(), o.lookup);
  }
}

/** Mark a settled token invalid, saying why. */
function invalidate(t: Token, message: string): void {
  t.status = 'invalid';
  t.message = message;
}

/** A `uses:`/`submodel:` token ANDed with the ones before it: the kind's alternatives after it, or null when it is over a limit. */
function andRefs(t: Token, before: Groups, groups: Groups, f: RefsField): Groups | null {
  const next = andGroups(before, groups);
  const word = f === 'uses' ? 'functional' : 'submodel';
  if (next.length > MAX_QUERY_GROUPS) {
    invalidate(t, `With the ${t.key}: filters before it, this makes ${next.length} alternatives: at most ${MAX_QUERY_GROUPS}`);
    return null;
  }
  if (next.some((g) => g.length > MAX_QUERY_REFS)) {
    invalidate(t, `With the ${t.key}: filters before it, a model would need more than ${MAX_QUERY_REFS} ${word}s at once`);
    return null;
  }
  return next;
}

/**
 * Cross-token rules, over settled tokens in order, marking the tokens they
 * refuse. A later single-valued filter replaces an earlier one, which is then
 * marked so. Returns each refs kind's alternatives: its tokens ANDed (#2299).
 */
function applyCrossTokenRules(tokens: Token[]): Record<RefsField, Groups> {
  const holder = new Map<Field, Token>();
  let anyGroup: Token | null = null;
  const counts = { 'tags.all': 0, 'tags.none': 0, authors: 0 };
  const refs: Record<RefsField, Groups> = { uses: [], submodels: [] };
  for (const t of tokens) {
    if (t.type !== 'filter' || t.active || t.status !== 'valid' || !t.value) continue;
    const f = t.value.field;
    if (SINGLE_FIELDS.has(f)) {
      const prev = holder.get(f);
      if (prev) invalidate(prev, `Replaced by ${t.raw} later in the search`);
      holder.set(f, t);
    } else if (f === 'tags.any') {
      if (anyGroup) invalidate(t, `Only one either-or tag group per search: join them, as in ${anyGroup.raw}|…`);
      else anyGroup = t;
    } else if (f === 'uses' || f === 'submodels') {
      refs[f] = andRefs(t, refs[f], t.value.groups, f) ?? refs[f];
    } else if (f === 'tags.all' || f === 'tags.none' || f === 'authors') {
      counts[f] += 1;
      const max = f === 'authors' ? MAX_QUERY_AUTHORS : MAX_QUERY_TAGS;
      if (counts[f] > max) invalidate(t, f === 'authors' ? `At most ${MAX_QUERY_AUTHORS} authors` : `At most ${MAX_QUERY_TAGS} tags of this kind`);
    }
  }
  return refs;
}

/** What one valid token adds to the query; `uses`/`submodels` are ANDed apart. */
function addToQuery(query: SearchQuery, v: TokenValue): void {
  switch (v.field) {
    case 'tags.all': case 'tags.any': case 'tags.none': {
      const bucket = query.tags[v.field.slice(5) as 'all' | 'any' | 'none'];
      for (const tag of v.tags) if (!bucket.includes(tag)) bucket.push(tag);
      break;
    }
    case 'authors': if (!query.authors.includes(v.id)) query.authors.push(v.id); break;
    case 'updated': query.updated = v.date; break;
    case 'created': query.created = v.date; break;
    case 'pick': query.editorsPick = true; break;
    case 'shared': query.shared = v.id; break;
    case 'uses': case 'submodels': break; // ANDed by applyCrossTokenRules
    case 'sort': query.sort = v.sort; break;
  }
}

/** Parse the box's text: every token with its status, and the search they make. */
export function parseSearch(text: string, options: ParseOptions = {}): ParsedSearch {
  const o = { lookup: options.lookup ?? NO_LOOKUP, today: options.today ?? todayUtc() };
  const { caret = null, editAt = null } = options;
  const isActive = (lx: Lexeme) =>
    caret !== null && editAt !== null && lx.start < caret && caret <= lx.end && lx.start < editAt && editAt <= lx.end;

  const tokens: Token[] = lex(text).map((lx) => {
    const k = keyOf(lx.raw);
    if (!k) return { ...lx, type: 'word', status: 'valid', active: false };
    return { ...readFilter(lx, k.key, k.negated, k.valueStart, o), active: isActive(lx) };
  });

  const refs = applyCrossTokenRules(tokens);

  const query: SearchQuery = structuredCloneQuery(EMPTY_QUERY);
  const words: string[] = [];
  let pending = false;
  for (const t of tokens) {
    if (t.type === 'word') { words.push(t.raw); continue; }
    if (t.active) continue;
    if (t.status === 'pending') pending = true;
    if (t.status === 'valid' && t.value) addToQuery(query, t.value);
  }
  query.functionals = refs.uses;
  query.submodels = refs.submodels;
  query.text = words.join(' ');
  const problems = query.text.length > MAX_QUERY_TEXT
    ? [`The search text is ${query.text.length} characters long: at most ${MAX_QUERY_TEXT}`]
    : [];
  return { tokens, query, pending, problems };
}

function structuredCloneQuery(q: SearchQuery): SearchQuery {
  return {
    ...q,
    tags: { all: [...q.tags.all], any: [...q.tags.any], none: [...q.tags.none] },
    authors: [...q.authors],
    functionals: q.functionals.map((g) => [...g]),
    submodels: q.submodels.map((g) => [...g]),
  };
}

/**
 * The tokens that are filters in force: what the chips show. The functionals
 * and the submodels are one chip per alternative of the whole search (#2299),
 * where their first token is: "Uses: Geo Buffer and Lookup", "Or uses: Join".
 */
export function chipTokens(parsed: ParsedSearch): Token[] {
  const settled = parsed.tokens.filter((t) => t.type === 'filter' && !t.active && t.status === 'valid');
  const labels: Record<string, string> = Object.assign({}, ...settled.map((t) => t.labels ?? {}));
  const done = new Set<RefsField>();
  const out: Token[] = [];
  for (const t of settled) {
    const f = t.field;
    if (f !== 'uses' && f !== 'submodels') { out.push(t); continue; }
    if (done.has(f)) continue;
    done.add(f);
    const groups = f === 'uses' ? parsed.query.functionals : parsed.query.submodels;
    groups.forEach((g, i) => out.push({
      ...t,
      group: i,
      chip: { name: REFS_CHIP[f][i === 0 ? 0 : 1], value: describeGroups([g], (id) => labels[id] ?? '…') },
    }));
  }
  return out;
}

/** The text without one alternative of `uses`/`submodels`: what its chip's ✕ does (#2299). */
export function removeGroup(text: string, field: RefsField, index: number, lookup: EntityLookup = NO_LOOKUP): string {
  const q = parseSearch(text, { lookup }).query;
  const key = field === 'uses' ? 'functionals' : 'submodels';
  return setField(text, field, { ...q, [key]: q[key].filter((_, i) => i !== index) }, lookup);
}

/** A chip's words: "Tag: port-ops", or "Editor's picks". */
export function chipText(t: Token): string {
  if (!t.chip) return t.raw;
  return t.chip.value ? `${t.chip.name}: ${t.chip.value}` : t.chip.name;
}

/** Plain-words problems to show beside the box: invalid settled tokens, then the whole search's. */
export function tokenProblems(parsed: ParsedSearch): string[] {
  const out: string[] = [];
  for (const t of parsed.tokens) {
    if (t.type === 'filter' && !t.active && t.status === 'invalid' && t.message) out.push(`${t.raw}: ${t.message}`);
  }
  return [...out, ...parsed.problems];
}

// ── The query, for the server and for comparison ────────────────────────

function toRange(f: DateFilter | null): QueryRange | null {
  if (!f) return null;
  if ('within' in f) return { within: f.within };
  return { ...(f.from ? { from: f.from } : {}), ...(f.to ? { to: f.to } : {}) };
}

/** The query as `POST api/models/query` takes it, empty parts left out. */
export function toModelQuery(q: SearchQuery): ModelQuery {
  const out: ModelQuery = { v: MODEL_QUERY_VERSION, kind: 'model' };
  if (q.text.trim()) out.text = q.text.trim();
  if (q.tags.all.length || q.tags.any.length || q.tags.none.length) {
    out.tags = {
      ...(q.tags.all.length ? { all: q.tags.all } : {}),
      ...(q.tags.any.length ? { any: q.tags.any } : {}),
      ...(q.tags.none.length ? { none: q.tags.none } : {}),
    };
  }
  if (q.authors.length) out.authors = q.authors;
  if (q.updated) out.updated = toRange(q.updated);
  if (q.created) out.created = toRange(q.created);
  if (q.editorsPick) out.editorsPick = true;
  if (q.shared) out.shared = q.shared;
  // One functional is the server's canonical `usesFunctional` (#2299).
  if (q.functionals.length === 1 && q.functionals[0].length === 1) out.usesFunctional = q.functionals[0][0];
  else if (q.functionals.length) out.functionals = q.functionals;
  if (q.submodels.length) out.submodels = q.submodels;
  if (q.sort) out.sort = q.sort;
  return out;
}

/** A stable key for a query: equal searches, equal keys. */
export function queryKey(q: SearchQuery): string {
  return JSON.stringify(toModelQuery(q));
}

/** Nothing to search for: Discover shows its sections. */
export function isEmptyQuery(q: SearchQuery): boolean {
  return queryKey(q) === queryKey(EMPTY_QUERY);
}

/** The search minus its text: comparing two tells a filter change from typing. */
export function filtersKey(q: SearchQuery): string {
  return queryKey({ ...q, text: '' });
}

// ── Writing the text ────────────────────────────────────────────────────

/** The tokens that write one field's value. */
export function tokensForField(field: Field, q: SearchQuery, lookup: EntityLookup = NO_LOOKUP): string[] {
  const named = (kind: EntityKind, key: string, id: string) => `${key}:${quote(lookup.nameFor(kind, id) ?? id)}`;
  // one token for all of a kind's alternatives, each name as the box would show it
  const grouped = (kind: EntityKind, key: string, groups: Groups) =>
    (groups.length ? [`${key}:${writeGroups(groups.map((g) => g.map((id) => lookup.nameFor(kind, id) ?? id)))}`] : []);
  switch (field) {
    case 'text': return q.text.trim() ? [q.text.trim()] : [];
    case 'tags.all': return q.tags.all.map((t) => `tag:${quote(t)}`);
    case 'tags.any': return q.tags.any.length ? [`tag:${q.tags.any.map(quote).join('|')}`] : [];
    case 'tags.none': return q.tags.none.map((t) => `-tag:${quote(t)}`);
    case 'authors': return q.authors.map((id) => (id === 'me' ? 'author:me' : named('author', 'author', id)));
    case 'updated': return q.updated ? [`updated:${formatDateValue(q.updated)}`] : [];
    case 'created': return q.created ? [`created:${formatDateValue(q.created)}`] : [];
    case 'pick': return q.editorsPick ? ['is:pick'] : [];
    case 'shared':
      if (!q.shared) return [];
      return q.shared === 'me' ? ['is:shared'] : [named('group', 'group', q.shared)];
    case 'uses': return grouped('functional', 'uses', q.functionals);
    case 'submodels': return grouped('model', 'submodel', q.submodels);
    case 'sort': return q.sort ? [`sort:${q.sort}`] : [];
  }
}

const FIELD_ORDER: Field[] = [
  'text', 'tags.all', 'tags.any', 'tags.none', 'authors', 'updated', 'created', 'pick', 'shared',
  'uses', 'submodels', 'sort',
];

/** A whole search as text, in the canonical order. */
export function serializeQuery(q: SearchQuery, lookup: EntityLookup = NO_LOOKUP): string {
  return joinTokens(FIELD_ORDER.flatMap((f) => tokensForField(f, q, lookup)));
}

/** Tokens joined by single spaces, with a trailing space after a filter so it is settled, not being typed. */
function joinTokens(parts: string[]): string {
  const text = parts.join(' ');
  if (!parts.length) return '';
  const last = lex(parts.at(-1)!);
  return last.length && keyOf(last.at(-1)!.raw) ? `${text} ` : text;
}

/** Whether a token belongs to a field. Plain words are the text. */
function inField(t: Token, field: Field): boolean {
  return field === 'text' ? t.type === 'word' : t.field === field;
}

/**
 * Replace every token of one field with new ones, written where the first of
 * them was (else the text goes first and a filter last). The rest of the text
 * is kept as typed.
 */
export function replaceField(text: string, field: Field, replacement: string[]): string {
  const tokens = parseSearch(text).tokens;
  const parts: string[] = [];
  let placed = false;
  for (const t of tokens) {
    if (inField(t, field)) {
      if (!placed) { parts.push(...replacement); placed = true; }
      continue;
    }
    parts.push(t.raw);
  }
  if (!placed) {
    if (field === 'text') parts.unshift(...replacement);
    else parts.push(...replacement);
  }
  return joinTokens(parts);
}

/** The text without one token (by its start offset). */
export function removeToken(text: string, start: number): string {
  return joinTokens(lex(text).filter((t) => t.start !== start).map((t) => t.raw));
}

/** The text without any filter token: only its words. */
export function clearFilters(text: string): string {
  return joinTokens(parseSearch(text).tokens.filter((t) => t.type === 'word').map((t) => t.raw));
}

/** Set one field of a query: what a panel field does, through `replaceField`. */
export function setField(text: string, field: Field, q: SearchQuery, lookup: EntityLookup = NO_LOOKUP): string {
  return replaceField(text, field, tokensForField(field, q, lookup));
}

/**
 * The text with every token naming `id` (an `author:`, `uses:`, `submodel:`
 * or `group:` written as an id, alone or in an either-or group) rewritten to
 * name it by `name`: a link's ids, once their labels are known, read as
 * names in the box. The search is unchanged.
 */
export function renameEntity(text: string, kind: EntityKind, id: string, name: string): string {
  let out = '';
  let at = 0;
  for (const lx of lex(text)) {
    const k = keyOf(lx.raw);
    if (!k || k.negated || ENTITY_OF[k.key] !== kind) continue;
    const raw = lx.raw.slice(k.valueStart);
    const same = (p: string) => p.toLowerCase() === id.toLowerCase();
    let value: string;
    if (takesGroups(k.key)) {
      const { groups, unclosed, malformed, empty } = readGroups(raw);
      if (unclosed || malformed || empty || !groups.flat().some(same)) continue;
      value = writeGroups(groups.map((g) => g.map((p) => (same(p) ? name : p))));
    } else {
      const { parts, unclosed, malformed } = readValue(raw, false);
      if (unclosed || malformed || parts.length !== 1 || !same(parts[0])) continue;
      value = quote(name);
    }
    out += text.slice(at, lx.start) + lx.raw.slice(0, k.valueStart) + value;
    at = lx.end;
  }
  return at === 0 ? text : out + text.slice(at);
}
